/** Las piezas pequeñas que se repiten: etiquetas, estados vacíos, diálogos y avisos. */
import { useEffect, type ReactNode } from 'react'

import { ESTADO_SOLICITUD, ESTADO_TAREA, PRIORIDAD } from '../textos'
import type { EstadoSolicitud, EstadoTarea, Prioridad } from '../tipos'

export function EtiquetaDePrioridad({ valor }: { valor: Prioridad | null }) {
  if (!valor) return <span className="etiqueta">Sin decidir</span>
  return (
    <span className={`etiqueta prio--${valor}`}>
      <span className="punto" aria-hidden="true" />
      {PRIORIDAD[valor]}
    </span>
  )
}

export function EtiquetaDeEstado({ texto }: { texto: string }) {
  return <span className="etiqueta">{texto}</span>
}

export function estadoSolicitud(valor: EstadoSolicitud) {
  return ESTADO_SOLICITUD[valor] ?? valor
}

export function estadoTarea(valor: EstadoTarea) {
  return ESTADO_TAREA[valor] ?? valor
}

export function Cargando({ filas = 3 }: { filas?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="cargando">Cargando…</span>
      {Array.from({ length: filas }, (_, i) => (
        <div className="esqueleto" key={i} />
      ))}
    </div>
  )
}

export function Vacio({ titulo, children }: { titulo: string; children?: ReactNode }) {
  return (
    <div className="vacio">
      <div className="vacio__titulo">{titulo}</div>
      {children}
    </div>
  )
}

export function Error({ children }: { children: ReactNode }) {
  return (
    <div className="error" role="alert">
      {children}
    </div>
  )
}

export function Dialogo({
  titulo,
  children,
  pie,
  alCerrar,
}: {
  titulo: string
  children: ReactNode
  pie?: ReactNode
  alCerrar: () => void
}) {
  // Escape cierra: es lo que cualquiera intenta primero.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
    }
    document.addEventListener('keydown', alPulsar)
    return () => document.removeEventListener('keydown', alPulsar)
  }, [alCerrar])

  return (
    <div className="velo" onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}>
      <div className="dialogo" role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="dialogo__cabecera">
          <h2>{titulo}</h2>
          <button
            className="boton boton--plano"
            style={{ marginLeft: 'auto' }}
            onClick={alCerrar}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="dialogo__cuerpo">{children}</div>
        {pie && <div className="dialogo__pie">{pie}</div>}
      </div>
    </div>
  )
}

export function Cifra({
  valor,
  etiqueta,
  alerta = false,
}: {
  valor: number
  etiqueta: string
  alerta?: boolean
}) {
  return (
    <div className={`tarjeta cifra${alerta && valor > 0 ? ' cifra--alerta' : ''}`}>
      <div className="cifra__valor">{valor}</div>
      <div className="cifra__etiqueta">{etiqueta}</div>
    </div>
  )
}
